import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../../../Components/Index";
import { getCustomers, getCustomerVehicles } from "../../../services/api/customerApi";
import { getMyMechanicProfile } from "../../../services/api/mechanicApi";
import { getServiceCategories } from "../../../services/api/serviceApi";
import { extractErrorMessage } from "../../../services/api/errorHandler";
import RecentServiceIcon from "./RecentServiceIcon";
import WorkerRecentServiceCard from "./WorkerRecentServiceCard";

const customerName = (customer) => [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") || "مشتری";

async function loadVehicleOwners() {
  const first = await getCustomers({ page: 1, sort: "newest", vehicles: "with" });
  const pages = Math.ceil(first.total / first.pageSize);
  const extraPages = pages > 1 ? await Promise.all(Array.from({ length: pages - 1 }, (_, index) => getCustomers({ page: index + 2, sort: "newest", vehicles: "with" }))) : [];
  const customers = [first, ...extraPages].flatMap((page) => page.data || []);
  const owners = new Map();
  for (let index = 0; index < customers.length; index += 8) {
    const batch = await Promise.all(customers.slice(index, index + 8).map(async (customer) => {
      try { return { customer, vehicles: await getCustomerVehicles(customer.id) }; }
      catch { return { customer, vehicles: [] }; }
    }));
    batch.forEach(({ customer, vehicles }) => vehicles.forEach((vehicle) => owners.set(String(vehicle.id), { vehicle, customer })));
  }
  return owners;
}

export default function WorkerRecentServicesView() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    Promise.all([getMyMechanicProfile(), getServiceCategories(), loadVehicleOwners()])
      .then(([profileResponse, categoryResponse, owners]) => {
        if (cancelled) return;
        const categories = new Map((categoryResponse?.data || []).map((item) => [String(item.id), item]));
        const logs = Array.isArray(profileResponse?.data?.serviceLogs) ? profileResponse.data.serviceLogs : [];
        const rows = logs.map((log) => {
          const owner = owners.get(String(log.vehicleId));
          return { ...log, title: log.details?.serviceTitle || categories.get(String(log.categoryId))?.title || "سرویس خودرو", vehicle: owner?.vehicle, customerName: customerName(owner?.customer) };
        }).sort((a, b) => new Date(b.serviceDate) - new Date(a.serviceDate));
        setServices(rows);
      })
      .catch((requestError) => { if (!cancelled) setError(extractErrorMessage(requestError, "دریافت سرویس‌های اخیر ممکن نشد")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [retry]);

  const visibleServices = useMemo(() => {
    const now = new Date();
    return services.filter((service) => {
      if (filter === "all") return true;
      const date = new Date(service.serviceDate);
      if (filter === "today") return date.toDateString() === now.toDateString();
      return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
    });
  }, [filter, services]);

  return <div className="worker-recent" dir="rtl">
    <Header Text="سرویس‌های اخیر" hideR="" LinkR="/ServiceDashbord" />
    <main className="worker-recent__main">
      <section className="worker-recent__hero"><span><RecentServiceIcon name="history" /></span><div><small>تاریخچه خدمات</small><h1>آخرین سرویس‌ها</h1><p>آخرین خدمات ثبت‌شده برای خودروهای مشتری‌ها</p></div><b>{services.length.toLocaleString("fa-IR")}</b></section>
      {!loading && !error && <div className="worker-recent__filters" role="group" aria-label="فیلتر زمانی">{[["all", "همه"], ["today", "امروز"], ["month", "این ماه"]].map(([value, label]) => <button type="button" key={value} className={filter === value ? "is-active" : ""} onClick={() => setFilter(value)}>{label}</button>)}</div>}
      {loading && <div className="worker-recent__skeletons" role="status">{[1, 2, 3].map((item) => <span key={item} />)}</div>}
      {!loading && error && <section className="worker-recent__state" role="alert"><RecentServiceIcon name="refresh" /><h2>دریافت اطلاعات ممکن نشد</h2><p>{error}</p><button type="button" onClick={() => setRetry((value) => value + 1)}>تلاش دوباره</button></section>}
      {!loading && !error && !visibleServices.length && <section className="worker-recent__state"><RecentServiceIcon name="empty" /><h2>{services.length ? "سرویسی در این بازه نیست" : "هنوز سرویسی ثبت نشده"}</h2><p>{services.length ? "یک بازه زمانی دیگر را انتخاب کنید." : "پس از ثبت اولین سرویس، اطلاعات آن اینجا نمایش داده می‌شود."}</p>{!services.length && <Link to="/PlateCapture"><RecentServiceIcon name="plus" />ثبت اولین سرویس</Link>}</section>}
      {!loading && !error && visibleServices.length > 0 && <><div className="worker-recent__result-head"><b>{visibleServices.length.toLocaleString("fa-IR")} سرویس</b><span>جدیدترین ثبت‌ها</span></div><div className="worker-recent__list">{visibleServices.map((service) => <WorkerRecentServiceCard key={service.id} service={service} />)}</div></>}
    </main>
  </div>;
}
